import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createSafetyIssue,
  deleteSafetyIssue,
  getSafetyIssues,
  getSafetyLayouts,
  getSafetyPhotoContext,
  getSafetyTrades,
  moveSafetyPin,
  updateSafetyIssue,
  updateSafetyIssueStatus,
} from './api';
import type {
  MoveSafetyPinInput,
  SafetyIssue,
  SafetyIssueFilters,
  SafetyIssueStatus,
  UpdateSafetyIssueInput,
} from './types';

export const safetyKeys = {
  all: ['safety'] as const,
  lists: (projectId: number) => [...safetyKeys.all, 'list', projectId] as const,
  list: (projectId: number, filters: SafetyIssueFilters) =>
    [...safetyKeys.lists(projectId), filters] as const,
  layouts: (projectId: number) => [...safetyKeys.all, 'layouts', projectId] as const,
  trades: (projectId: number) => [...safetyKeys.all, 'trades', projectId] as const,
  photoContext: (projectId: number, photoId: number) =>
    [...safetyKeys.all, 'photo-context', projectId, photoId] as const,
};

export const useSafetyIssues = (projectId: number | undefined, filters: SafetyIssueFilters = {}) =>
  useQuery({
    queryKey: safetyKeys.list(projectId!, filters),
    queryFn: () => getSafetyIssues(projectId!, filters),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });

export const useSafetyLayouts = (projectId: number | undefined) =>
  useQuery({
    queryKey: safetyKeys.layouts(projectId!),
    queryFn: () => getSafetyLayouts(projectId!),
    enabled: Boolean(projectId),
    staleTime: 5 * 60_000,
  });

export const useSafetyTrades = (projectId: number | undefined) =>
  useQuery({
    queryKey: safetyKeys.trades(projectId!),
    queryFn: () => getSafetyTrades(projectId!),
    enabled: Boolean(projectId),
    staleTime: 5 * 60_000,
  });

export const useSafetyPhotoContext = (
  projectId: number | undefined,
  photoId: number | undefined,
  enabled = true
) =>
  useQuery({
    queryKey: safetyKeys.photoContext(projectId!, photoId!),
    queryFn: () => getSafetyPhotoContext(projectId!, photoId!),
    enabled: enabled && Boolean(projectId && photoId),
    staleTime: 5 * 60_000,
  });

type SafetyListSnapshot = Array<[readonly unknown[], SafetyIssue[] | undefined]>;

export const useCreateSafetyIssue = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSafetyIssue,
    onMutate: async (input) => {
      if (!projectId) return { snapshots: [] as SafetyListSnapshot };
      const key = safetyKeys.lists(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const snapshots = queryClient.getQueriesData<SafetyIssue[]>({ queryKey: key });
      const now = new Date().toISOString();
      const optimistic: SafetyIssue = {
        id: `optimistic-${now}`,
        project: input.project,
        description: input.description,
        trade: input.trade ?? null,
        trade_detail: null,
        location: input.location ?? null,
        location_detail: null,
        hotspot: input.hotspot ?? null,
        layout: input.layout ?? null,
        position_x: input.position_x ?? null,
        position_y: input.position_y ?? null,
        status: 'OPEN',
        created_by: null,
        resolved_by: null,
        resolved_at: null,
        created_at: now,
        updated_at: now,
      };
      for (const [queryKey, current] of snapshots) {
        queryClient.setQueryData<SafetyIssue[]>(queryKey, [optimistic, ...(current ?? [])]);
      }
      return { snapshots };
    },
    onError: (_error, _input, context) => {
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: (_data, _error, input) => {
      if (projectId) void queryClient.invalidateQueries({ queryKey: safetyKeys.lists(projectId) });
      if (projectId && input.location_name) {
        void queryClient.invalidateQueries({ queryKey: safetyKeys.layouts(projectId) });
      }
    },
  });
};

export const useUpdateSafetyIssueStatus = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, status }: { issueId: string; status: SafetyIssueStatus }) =>
      updateSafetyIssueStatus(issueId, status),
    onMutate: async ({ issueId, status }) => {
      if (!projectId) return { snapshots: [] as SafetyListSnapshot };
      const key = safetyKeys.lists(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const snapshots = queryClient.getQueriesData<SafetyIssue[]>({ queryKey: key });
      for (const [queryKey, current] of snapshots) {
        queryClient.setQueryData<SafetyIssue[]>(
          queryKey,
          current?.map((issue) => (issue.id === issueId ? { ...issue, status } : issue))
        );
      }
      return { snapshots };
    },
    onError: (_error, _input, context) => {
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      if (projectId) void queryClient.invalidateQueries({ queryKey: safetyKeys.lists(projectId) });
    },
  });
};

export const useUpdateSafetyIssue = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, input }: { issueId: string; input: UpdateSafetyIssueInput }) =>
      updateSafetyIssue(issueId, input),
    onMutate: async ({ issueId, input }) => {
      if (!projectId) return { snapshots: [] as SafetyListSnapshot };
      const key = safetyKeys.lists(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const snapshots = queryClient.getQueriesData<SafetyIssue[]>({ queryKey: key });
      for (const [queryKey, current] of snapshots) {
        queryClient.setQueryData<SafetyIssue[]>(
          queryKey,
          current?.map((issue) =>
            issue.id === issueId
              ? {
                  ...issue,
                  ...input,
                  location_detail:
                    input.location === null || input.location_name ? null : issue.location_detail,
                }
              : issue
          )
        );
      }
      return { snapshots };
    },
    onError: (_error, _input, context) => {
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: (_data, _error, variables) => {
      if (projectId) void queryClient.invalidateQueries({ queryKey: safetyKeys.lists(projectId) });
      if (projectId && variables.input.location_name) {
        void queryClient.invalidateQueries({ queryKey: safetyKeys.layouts(projectId) });
      }
    },
  });
};

export const useMoveSafetyPin = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MoveSafetyPinInput) => moveSafetyPin(input),
    onMutate: async (input) => {
      if (!projectId) return { snapshots: [] as SafetyListSnapshot };
      const key = safetyKeys.lists(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const snapshots = queryClient.getQueriesData<SafetyIssue[]>({ queryKey: key });
      const movingIds = new Set(input.issue_ids);
      for (const [queryKey, current] of snapshots) {
        queryClient.setQueryData<SafetyIssue[]>(
          queryKey,
          current?.map((issue) =>
            movingIds.has(issue.id)
              ? {
                  ...issue,
                  position_x: input.position_x,
                  position_y: input.position_y,
                }
              : issue
          )
        );
      }
      return { snapshots };
    },
    onError: (_error, _input, context) => {
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      if (projectId) void queryClient.invalidateQueries({ queryKey: safetyKeys.lists(projectId) });
    },
  });
};

export const useDeleteSafetyIssue = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSafetyIssue,
    onMutate: async (issueId) => {
      if (!projectId) return { snapshots: [] as SafetyListSnapshot };
      const key = safetyKeys.lists(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const snapshots = queryClient.getQueriesData<SafetyIssue[]>({ queryKey: key });
      for (const [queryKey, current] of snapshots) {
        queryClient.setQueryData<SafetyIssue[]>(
          queryKey,
          current?.filter((issue) => issue.id !== issueId)
        );
      }
      return { snapshots };
    },
    onError: (_error, _issueId, context) => {
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      if (projectId) void queryClient.invalidateQueries({ queryKey: safetyKeys.lists(projectId) });
    },
  });
};
